/* eslint-disable react/jsx-sort-props */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { yupResolver } from '@hookform/resolvers/yup';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Checkbox, IconButton, Input, Typography } from '@mui/joy';
import { CircularProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { DroneProfile } from '../models/DroneProfile/DroneProfile.model';
import CampaignSchema from '../schema/campaignSchema';
import { CampaignService } from '../services/CampaignService';
import { DroneProfileService } from '../services/DroneProfileService';
import campaignStore from '../stores/CampaignStore';
import organizationStore from '../stores/OrganizationStore';
import sessionStore from '../stores/SessionStore';
import CampaignCard from './Campaign/CampaignCard';
import { ActionRadioField } from './FormElements/ActionRadioField';
import { SelectField } from './FormElements/SelectField';
import { TextAreaField } from './FormElements/TextAreaField';
import { TextInputField } from './FormElements/TextInputField';
import GeneralModal from './GeneralModal';
import TableHeader from './TableHeader';

interface CampaignFormInput {
  name: string;
  description: string;
  drone: DroneProfile;
  status: number;
  enable_vantis_send: boolean;
}

const CampaignList = observer(() => {
  const { campaigns } = campaignStore;
  const { loading } = campaignStore;
  const { error } = campaignStore;
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { selectedOrg } = organizationStore;
  const { user } = sessionStore;
  const navigate = useNavigate();
  const [drones, setDrones] = useState<DroneProfile[]>([]);
  const [loadingDrones, setLoadingDrones] = useState(true);
  const [page, setPage] = useState(1);
  const itemsPerPage = 6; // Number of campaigns per page
  const campaignOptions = [
    {
      label: t('campaigns.status_options.active'),
      value: 1,
      description: t('campaigns.status_options.active_description')
    },
    {
      label: t('campaigns.status_options.inactive'),
      value: 0,
      description: t('campaigns.status_options.inactive_description')
    }
  ];

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<CampaignFormInput>({
    resolver: yupResolver(CampaignSchema(t)),
    defaultValues: {
      name: '',
      description: '',
      drone: undefined,
      status: 1,
      enable_vantis_send: false
    }
  });

  useEffect(() => {
    campaignStore.fetchCampaigns(selectedOrg!, t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchDrones = async () => {
      await DroneProfileService.getOrganizationDroneProfiles(selectedOrg!).then((response) => {
        setDrones(response.data.drone_profiles);
        setLoadingDrones(false);
      });
    };
    fetchDrones();
  }, [selectedOrg]);

  const handleOpenModal = (): void => {
    setIsModalOpen(true);
  };

  const handleCloseModal = (): void => {
    reset();
    setIsModalOpen(false);
  };

  const handleAddCampaign = async (data: CampaignFormInput): Promise<void> => {
    if (selectedOrg === null) {
      toast.error(t('general_errors.no_organization_selected'));
      return;
    }

    try {
      const response = await CampaignService.createCampaign({
        ...data,
        drones: [data.drone.id],
        organization: selectedOrg!
      });
      campaignStore.updateCampaign(response);
      toast.success(t('campaigns.messages.campaign_created'));
      reset();
      setIsModalOpen(false);
    } catch {
      toast.error(t('campaigns.errors.failed_to_create_campaign'));
    }
  };

  const joinCampaign = async (campaignId: string): Promise<void> => {
    const foundCampaign = campaigns.find((campaign) => {
      if (campaign.id === campaignId) {
        return campaign;
      }
      return null;
    });
    if (!foundCampaign) {
      console.error('Campaign not found'); // this shouldn't happen.
      return;
    }
    navigate(`/campaign/${campaignId}`);
  };

  // Sort campaigns: active first, then by newest to oldest
  const sortedCampaigns = campaigns.slice().sort((a, b) => {
    // Active campaigns first
    if (a.status === 1 && b.status === 0) return -1;
    if (a.status === 0 && b.status === 1) return 1;
    
    // Then sort by start_time (newest first)
    const dateA = a.start_time ? new Date(a.start_time).getTime() : 0;
    const dateB = b.start_time ? new Date(b.start_time).getTime() : 0;
    return dateB - dateA;
  });

  const filteredCampaigns = sortedCampaigns.filter((campaign) =>
    campaign.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredCampaigns.length / itemsPerPage);
  const paginatedCampaigns = filteredCampaigns.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handlePageChange = (event: React.ChangeEvent<unknown> | null, value: number) => {
    setPage(value);
    // Scroll to top when changing pages
    window.scrollTo(0, 0);
  };

  return (
    <>
      <TableHeader title='Campaigns'>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, ml: 2 }}>
          <Input
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Search'
            startDecorator={<SearchIcon />}
            sx={{
              '--Input-minHeight': '2.5rem',
              maxWidth: '25rem',
              width: '100%'
            }}
            value={searchQuery}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 1 }}>
          <Button color='neutral' onClick={handleOpenModal}>
            {t('campaigns.add_campaign')}
          </Button>
        </Box>
      </TableHeader>

      <Box sx={{ padding: '20px', fontFamily: "'Arial', sans-serif" }}>
        {loading && (
          <Box sx={{ textAlign: 'center', padding: '50px' }}>
            <CircularProgress />
            <Typography level='body-md' marginTop='15px'>
              {t('loading')}
            </Typography>
          </Box>
        )}
        {!loading && error && (
          <Box sx={{ textAlign: 'center', padding: '50px' }}>
            <Typography color='danger' level='body-md'>
              {t('campaigns.loading_failed')}
            </Typography>
          </Box>
        )}
        {!loading && !error && (
          <>
            <Box
              sx={{
                display: 'grid',
                gap: '20px',
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(1, 1fr)',
                  md: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                  xl: 'repeat(3, 1fr)'  // Changed from 4 to 3 columns
                },
                padding: '20px'
              }}
            >
              {paginatedCampaigns.map((campaign) => (
                <CampaignCard key={campaign.id} user={user} campaign={campaign} joinCampaign={joinCampaign} />
              ))}
            </Box>
            
            {filteredCampaigns.length > itemsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 4 }}>
                <IconButton
                  disabled={page === 1}
                  onClick={() => handlePageChange(null, page - 1)}
                  variant="outlined"
                  color="neutral"
                  size="sm"
                >
                  <KeyboardArrowLeftIcon />
                </IconButton>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
                  <Typography level="body-md">
                    {`${(page - 1) * itemsPerPage + 1}-${Math.min(page * itemsPerPage, filteredCampaigns.length)} of ${filteredCampaigns.length}`}
                  </Typography>
                </Box>
                
                <IconButton
                  disabled={page === totalPages}
                  onClick={() => handlePageChange(null, page + 1)}
                  variant="outlined"
                  color="neutral"
                  size="sm"
                >
                  <KeyboardArrowRightIcon />
                </IconButton>
              </Box>
            )}
            
            {filteredCampaigns.length === 0 && (
              <Box sx={{ textAlign: 'center', padding: '50px' }}>
                <Typography level="body-md">
                  {t('campaigns.no_campaigns_found')}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      <GeneralModal
        actions={
          <>
            <Button color='neutral' onClick={handleCloseModal} variant='plain'>
              {t('general_button_labels.cancel')}
            </Button>
            <Button color='neutral' form='campaign-form' type='submit' variant='solid'>
              {t('general_button_labels.save')}
            </Button>
          </>
        }
        content={
          <form id='campaign-form' onSubmit={handleSubmit(handleAddCampaign)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, width: 550 }}>
              <Box>
                <Controller
                  control={control}
                  name='name'
                  render={({ field }) => (
                    <TextInputField
                      label='Campaign Name'
                      placeholder='Enter Campaign Name'
                      {...field}
                      error={errors.name?.message}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  control={control}
                  name='drone'
                  render={({ field }) => (
                    <SelectField
                      label={t('campaigns.drone_label')}
                      placeholder='Select Drone'
                      {...field}
                      error={errors.drone?.message}
                      options={drones.map((drone) => ({
                        label: `${drone.name} - ${drone.eid}`,
                        value: drone.id
                      }))}
                      value={field.value ? field.value.id : ''}
                      onChange={(selectedDroneId) => {
                        const selectedDrone = drones.find((drone) => drone.id === selectedDroneId);
                        field.onChange(selectedDrone);
                      }}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  control={control}
                  name='description'
                  render={({ field }) => (
                    <TextAreaField
                      label={t('campaigns.campaign_outline_label')}
                      placeholder='Enter Description'
                      minRows={4}
                      {...field}
                      error={errors.description?.message}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  control={control}
                  name='status'
                  render={({ field }) => (
                    <ActionRadioField
                      label={t('campaigns.status_label')}
                      {...field}
                      error={errors.status?.message}
                      options={campaignOptions.map((option) => ({
                        value: option.value.toString(),
                        label: option.label,
                        description: option.description
                      }))}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  control={control}
                  name='enable_vantis_send'
                  render={({ field }) => (
                    <Checkbox
                      component="span"
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      label={t('campaigns.enable_vantis')}
                    />
                  )}
                />
              </Box>
            </Box>
          </form>
        }
        onClose={handleCloseModal}
        open={isModalOpen}
        title={t('campaigns.add_title')}
      />
    </>
  );
});

export default CampaignList;
